import React, { useCallback, useEffect, useRef, useState } from "react";
import BackgroundImg from "../img/BackgroundNetImg.svg";
import Header from "./Header";
import { motion } from "framer-motion";
import api from "../../api/axiosInstance";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";

/* ------------ Animation Variants ------------ */
const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};
const boxVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: "easeOut" } },
};



const tenant_name = "demo"; // hard-coded as requested

const TABS = { BULK: "bulk", INDIVIDUAL: "individual" };
const BULK_API = "/display/api/v1/list-referral-users"; // DRF paginated
const INDIVIDUAL_API =
  "https://stage-nicedb.gksinfotech.com/api/candidateregistration/users_by_referral"; // non-paginated

export default function Dashboard() {
  const navigate = useNavigate();
  const userid = localStorage.getItem("user_id");
  const location = useLocation();
  const referralCode = location.state?.referralCode || "";

  /* ------------ Top metrics / referral count ------------ */
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralCount, setReferralCount] = useState(null);
  const [referralError, setReferralError] = useState("");

  /* ------------ Tabs ------------ */
  const [activeTab, setActiveTab] = useState(TABS.BULK);

  /* ------------ Bulk users (server pagination) ------------ */
  const [usersBulk, setUsersBulk] = useState([]);
  const [loadingBulk, setLoadingBulk] = useState(false);
  const [errorBulk, setErrorBulk] = useState("");
  const [nextBulk, setNextBulk] = useState(null);
  const [countBulk, setCountBulk] = useState(0);

  /* ------------ Individual users (client windowed) ------------ */
  const [usersInd, setUsersInd] = useState([]);
  const [loadingInd, setLoadingInd] = useState(false);
  const [errorInd, setErrorInd] = useState("");
  const [countInd, setCountInd] = useState(0);
  const [indVisible, setIndVisible] = useState(30); // initial visible rows
  const IND_STEP = 30; // how many to reveal per step

  /* ------------ Refs for inner scrolling + sentinel + guard ------------ */
  const listRef = useRef(null); // scrollable table container
  const endRef = useRef(null); // sentinel at bottom
  const isFetchingNext = useRef(false); // prevents duplicate fetches

  /* ------------ Scroll top on page mount ------------ */
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* ------------ Referral count (only for user_id === "91") ------------ */
  useEffect(() => {
    const run = async () => {
      try {
        setReferralLoading(true);
        setReferralError("");
        const token = localStorage.getItem("accessToken");
        const payload = { referral_code_list: [referralCode.toLowerCase()] };

        const res = await api.post(
          "/admin-app/api/v1/get-referral-count",
          payload,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        setReferralCount(res?.data?.data ?? 0);
      } catch (err) {
        setReferralError(
          (err &&
            err.response &&
            err.response.data &&
            err.response.data.message) ||
            err?.message ||
            "Failed to fetch referral count",
        );
        setReferralCount(0);
      } finally {
        setReferralLoading(false);
      }
    };

    run();
  }, [userid]);

  /* ------------ First page fetchers ------------ */
  const fetchFirstBulk = useCallback(async () => {
    try {
      setLoadingBulk(true);
      setErrorBulk("");

      const codeToUse = referralCode.toUpperCase();

      const { data } = await api.get(BULK_API, {
        params: { code: codeToUse, page: 1 },
      });

      const page = data?.data;
      const results = Array.isArray(page?.results) ? page.results : [];

      setUsersBulk(results);
      setNextBulk(page?.next ?? null);
      setCountBulk(
        typeof page?.count === "number" ? page.count : results.length,
      );
    } catch (err) {
      setUsersBulk([]);
      setNextBulk(null);
      setCountBulk(0);
      setErrorBulk(err?.message || "Failed to load users");
    } finally {
      setLoadingBulk(false);
    }
  }, [userid]);

  const fetchFirstIndividual = useCallback(async () => {
    try {
      setLoadingInd(true);
      setErrorInd("");

      const finalReferral = referralCode.toLowerCase();

      const { data: resp } = await axios.get(INDIVIDUAL_API, {
        params: { referral_code: finalReferral, tenant_name },
      });

      const list = Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(resp)
          ? resp
          : [];

      setUsersInd(list);
      setCountInd(list.length);
      setIndVisible(Math.min(IND_STEP, list.length));
    } catch (err) {
      setUsersInd([]);
      setCountInd(0);
      setErrorInd(err?.message || "Failed to load users");
    } finally {
      setLoadingInd(false);
    }
  }, [userid]);

  /* ------------ Initial fetch (bulk default) ------------ */
  useEffect(() => {
    // if (String(userid) !== "91") return;
    fetchFirstBulk();
  }, [userid, fetchFirstBulk, fetchFirstIndividual]);

  /* ------------ When switching tabs, fetch if empty ------------ */
  useEffect(() => {
    if (activeTab === TABS.BULK && usersBulk.length === 0 && !loadingBulk) {
      fetchFirstBulk();
    }
    if (activeTab === TABS.INDIVIDUAL && usersInd.length === 0 && !loadingInd) {
      fetchFirstIndividual();
    }
  }, [activeTab, userid]);

  /* ------------ Helpers ------------ */
  const parsePageFromNext = (urlStr) => {
    if (!urlStr) return null;
    try {
      const u = new URL(urlStr);
      const p = u.searchParams.get("page");
      return p ? Number(p) : null;
    } catch {
      const m = urlStr.match(/[?&]page=(\d+)/);
      return m ? Number(m[1]) : null;
    }
  };

  /* ------------ Load next logic (bulk = server; individual = client window) ------------ */
  const loadNext = useCallback(async () => {
    if (activeTab === TABS.BULK) {
      if (!nextBulk || loadingBulk) return;
      if (isFetchingNext.current) return;
      isFetchingNext.current = true;

      try {
        setLoadingBulk(true);

        const codeToUse = referralCode.toUpperCase();
        const pageNum = parsePageFromNext(nextBulk);

        const { data } = await api.get(BULK_API, {
          params: { code: codeToUse, ...(pageNum ? { page: pageNum } : {}) },
        });

        const page = data?.data;
        const results = Array.isArray(page?.results) ? page.results : [];

        setUsersBulk((prev) => {
          if (!results.length) return prev;
          if (results[0]?.id == null) return [...prev, ...results];
          const seen = new Set(prev.map((x) => x.id));
          const merged = [...prev];
          for (const r of results) if (!seen.has(r.id)) merged.push(r);
          return merged;
        });

        setNextBulk(page?.next ?? null);
        setCountBulk(typeof page?.count === "number" ? page.count : countBulk);
      } catch (err) {
        setErrorBulk(err?.message || "Failed to load more users");
      } finally {
        setLoadingBulk(false);
        isFetchingNext.current = false;
      }
    } else {
      // INDIVIDUAL: reveal next window locally
      if (loadingInd) return;
      if (indVisible >= usersInd.length) return;
      setIndVisible((v) => Math.min(v + IND_STEP, usersInd.length));
    }
  }, [
    activeTab,
    countBulk,
    indVisible,
    loadingBulk,
    loadingInd,
    nextBulk,
    usersInd.length,
    userid,
  ]);

  /* ------------ IntersectionObserver on inner container ------------ */
  useEffect(() => {
    const sc = listRef.current;
    const sentinel = endRef.current;
    if (!sc || !sentinel) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        if (!visible) return;

        const canBulk = activeTab === TABS.BULK && nextBulk && !loadingBulk;
        const canInd =
          activeTab === TABS.INDIVIDUAL &&
          indVisible < usersInd.length &&
          !loadingInd;

        if (canBulk || canInd) loadNext();
      },
      { root: sc, rootMargin: "200px", threshold: 0 }, // inner scroll root
    );

    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [
    activeTab,
    nextBulk,
    loadingBulk,
    loadingInd,
    indVisible,
    usersInd.length,
    loadNext,
    userid,
  ]);

  /* ------------ Fallback onScroll (helps on some browsers) ------------ */
  const handleInnerScroll = () => {
    const sc = listRef.current;
    if (!sc) return;
    const { scrollTop, clientHeight, scrollHeight } = sc;
    if (scrollTop + clientHeight >= scrollHeight * 0.85) {
      loadNext();
    }
  };

  /* ------------ Metrics ------------ */
  const unitsSoldDisplay = referralCode
    ? referralLoading
      ? "Loading..."
      : (referralCount ?? 0)
    : "0";

  const metrics = [
    { title: "Total Revenue", value: "₹ 0", change: "▲ 0%" },
    { title: "Units Sold", value: unitsSoldDisplay, change: "▼ 0%" },
    { title: "Avg. Unit Price", value: "₹ 0", change: "▲ 0%" },
    { title: "Total Discounts", value: "₹ 0", change: "▼ 0%" },
  ];

  /* ------------ Table helpers ------------ */
  const currentRows =
    activeTab === TABS.BULK ? usersBulk : usersInd.slice(0, indVisible);
  const currentLoading = activeTab === TABS.BULK ? loadingBulk : loadingInd;
  const currentError = activeTab === TABS.BULK ? errorBulk : errorInd;
  const currentCount = activeTab === TABS.BULK ? countBulk : countInd;

  return (
    <div>
      <Header />
      <div
        className="bg-white bg-cover bg-center"
        style={{
          backgroundImage: `url(${BackgroundImg})`,
          backgroundPosition: "50% 0%",
        }}
      >
        {/* page wrapper is NOT scroll-locked; the inner table is */}
        <div className="flex flex-col px-4 md:px-8 lg:px-10 py-6 space-y-6">
          {/* Welcome */}
          <div>
            <h1 className="text-2xl font-bold text-black font-inter mb-1">
              Welcome!
            </h1>
            <p className="text-sm text-black max-w-xl">
              Here’s today’s revenue chart – track your earnings and performance
              at a glance.
            </p>
          </div>

          {/* Metrics */}
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {metrics.map((box, index) => (
              <motion.div
                key={index}
                className="bg-white border rounded-lg p-4 flex flex-col justify-between h-[150px]"
                variants={boxVariants}
                whileHover={{ scale: 1.06 }}
                transition={{ type: "spring", stiffness: 300 }}
                style={{ boxShadow: "0px 0px 4px 0px #00000040" }}
              >
                <h3 className="text-lg font-bold text-black font-inter mb-2">
                  {box.title}
                </h3>
                <div className="flex justify-between gap-2">
                  <div className="flex-1">
                    <h4 className="text-xs font-semibold text-black mb-1">
                      Value
                    </h4>
                    <div className="bg-[#F1EFFF] rounded-md px-2 py-2 text-sm font-bold text-[#4C3A68]">
                      {box.value}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-xs font-semibold text-black mb-1">
                      Change
                    </h4>
                    <div className="bg-[#F1EFFF] rounded-md px-2 py-2 text-sm font-bold text-[#4C3A68]">
                      {box.change}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Users (Tabs) — only for user_id 91 */}
          {referralCode && (
            <motion.div
              className="bg-white border rounded-lg"
              initial={{ opacity: 0, y: 12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              style={{ boxShadow: "0px 0px 4px 0px #00000040" }}
            >
              {/* Tabs header */}
              <div className="px-4 pt-3">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setActiveTab(TABS.BULK)}
                    className={`px-3 py-2 text-sm rounded-md border ${
                      activeTab === TABS.BULK
                        ? "bg-[#F1EFFF] text-[#4C3A68] border-[#E4DDFB]"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Purchased Users
                  </button>
                  <button
                    onClick={() => setActiveTab(TABS.INDIVIDUAL)}
                    className={`px-3 py-2 text-sm rounded-md border ${
                      activeTab === TABS.INDIVIDUAL
                        ? "bg-[#F1EFFF] text-[#4C3A68] border-[#E4DDFB]"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Demo Users
                  </button>
                </div>
              </div>

              {/* Title */}
              <div className="px-4 pb-2 pt-2 font-semibold text-black flex items-center gap-2">
                <span>
                  {activeTab === TABS.BULK ? "Bulk Users" : "Individual Users"}
                </span>
                <span className="text-xs text-gray-500">({currentCount})</span>
              </div>

              {/* Scrollable table container (relative so sticky header pins correctly) */}
              <div
                ref={listRef}
                onScroll={handleInnerScroll}
                className="relative overflow-y-auto max-h-[60vh] overscroll-contain"
              >
                <table className="w-full table-auto border-collapse">
                  <thead className="sticky top-0 z-20 bg-[#F1EFFF] shadow-[0_1px_0_0_rgba(0,0,0,0.08)]">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium text-black border-b">
                        Sl. No
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-black border-b">
                        Name
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-black border-b">
                        Email
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-black border-b">
                        Phone
                      </th>
                      {activeTab !== TABS.BULK ?(
                      <th className="px-4 py-2 text-left text-sm font-medium text-black border-b">
                        Actions
                      </th>
                      ): (
                        <th className="px-4 py-2 text-left text-sm font-medium text-black border-b">
                      </th>
                      )}
                    </tr>
                  </thead>

                  <tbody className="text-sm text-gray-800">
                    {/* Loading empty */}
                    {currentLoading && currentRows.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-3 text-gray-600">
                          Loading...
                        </td>
                      </tr>
                    )}

                    {/* Error empty */}
                    {currentError &&
                      currentRows.length === 0 &&
                      !currentLoading && (
                        <tr>
                          <td colSpan={5} className="px-4 py-3 text-red-600">
                            {currentError}
                          </td>
                        </tr>
                      )}

                    {/* No data */}
                    {!currentLoading &&
                      !currentError &&
                      currentRows.length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-4 py-3 text-gray-600">
                            No users found
                          </td>
                        </tr>
                      )}

                    {/* Rows */}
                    {currentRows.map((u, idx) => {
                      const name = u.name ?? u.full_name ?? "—";
                      const email = u.email ?? u.email_id ?? "—";
                      const phone = u.phone ?? u.phone_number ?? "—";
                      return (
                        <tr
                          key={
                            (u.id ?? `${email}-${phone}-${idx}`) +
                            (activeTab === TABS.BULK ? "-bulk" : "-ind")
                          }
                          className="hover:bg-gray-50"
                        >
                          <td className="px-4 py-2 border-b">{idx + 1}</td>
                          <td className="px-4 py-2 border-b break-all">
                            {name}
                          </td>
                          <td className="px-4 py-2 border-b break-all">
                            {email}
                          </td>
                          <td className="px-4 py-2 border-b break-all">
                            {phone}
                          </td>
                          <td className="px-4 py-2 border-b">
                            {u?.participant_info ? (
                              <button
                                onClick={() =>
                                  navigate("/participantdetails", {
                                    state: { user: u },
                                  })
                                }
                                className="text-primary underline"
                              >
                                View
                              </button>
                            ) : (
                              <span className="text-gray-400"></span>
                            )}
                          </td>
                        </tr>
                      );
                    })}

                    {/* Loading more (bulk) */}
                    {activeTab === TABS.BULK &&
                      loadingBulk &&
                      usersBulk.length > 0 && (
                        <tr>
                          <td colSpan={5} className="px-4 py-3 text-gray-500">
                            Loading more…
                          </td>
                        </tr>
                      )}

                    {/* Load-more error after rows (bulk) */}
                    {activeTab === TABS.BULK &&
                      errorBulk &&
                      usersBulk.length > 0 &&
                      !loadingBulk && (
                        <tr>
                          <td colSpan={5} className="px-4 py-3 text-red-600">
                            {errorBulk}
                          </td>
                        </tr>
                      )}

                    {/* Reveal more hint (individual) */}
                    {activeTab === TABS.INDIVIDUAL &&
                      indVisible < usersInd.length && (
                        <tr>
                          <td colSpan={5} className="px-4 py-3 text-gray-500">
                            Scroll to load more…
                          </td>
                        </tr>
                      )}
                  </tbody>
                </table>

                {/* Manual "Load more" for Bulk (optional, useful for debugging UX) */}
                {activeTab === TABS.BULK && nextBulk && !loadingBulk && (
                  <div className="px-4 py-3">
                    <button
                      onClick={loadNext}
                      className="px-3 py-1 border rounded hover:bg-gray-50"
                    >
                      Load more
                    </button>
                  </div>
                )}

                {/* Sentinel must be inside the scrollable container */}
                <div ref={endRef} className="h-16 w-full" />
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
